// Prompts the user to reload when the deployed build no longer matches the
// one they are running. Mounted once, inside the authenticated app.
//
// Non-blocking and dismissible by design. It NEVER reloads on its own:
// someone mid-way through configuring an automation would lose unsaved
// work, which is a worse bug than the stale UI it is warning about.
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  CHECK_INTERVAL_MS,
  buildId,
  fetchCurrentBuildId,
  isStale,
} from "../lib/buildVersion";

const TOAST_ID = "tadlo-new-version";

export default function NewVersionToast() {
  const location = useLocation();
  // Once shown, never re-shown for the same session. Sonner would dedupe by
  // id anyway, but a dismissed toast reappearing on every route change is
  // the fastest way to teach someone to ignore it.
  const shownRef = useRef(false);
  const mineRef = useRef(buildId());

  const check = useCallback(async () => {
    if (shownRef.current) return;
    // No baked id (dev server, or a build that predates write-version.js)
    // means we cannot compare. Say nothing rather than guess.
    if (!mineRef.current) return;

    const current = await fetchCurrentBuildId();
    if (!isStale(mineRef.current, current)) return;

    shownRef.current = true;
    toast("A new version of Tadlo is available.", {
      id: TOAST_ID,
      description: "Reload to get the latest.",
      duration: Infinity,          // stays until acted on or dismissed
      closeButton: true,
      action: {
        label: "Reload",
        onClick: () => window.location.reload(),
      },
    });
  }, []);

  // On navigation — the cheapest moment to notice, and the one just before
  // the user starts configuring something.
  useEffect(() => { check(); }, [location.pathname, check]);

  // On returning to the tab, and on a slow interval, for the tab that has
  // been open for days without navigating. Mirrors the refresh triggers
  // useFeatureAccess already uses.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(id);
    };
  }, [check]);

  return null;
}
